import { useNavigate, useSearchParams } from 'react-router-dom'
import NameValueTable from '../components/ui-components/NameValueTable'
import NameValueTableRow, {
  GROUPED_STATIC_TEXT_ROLE
} from '../components/ui-components/NameValueTableRow'
import BackButton from '../components/ui-components/BackButton'
import RenderHTML from '../components/ui-components/RenderHTML'
import { MUTUAL_FUND_DETAIL_IMAGE_URL } from '../constants'
import { throttle } from '../utils/utils'
import { useMemo, useState } from 'react'
import { goBack, goToHome } from '../utils/appBridge'
import { getBottomPadding, getTopPadding } from '../utils/sessionStorage'
import { useSetAppBarTheme } from '../hooks/useSetAppBarTheme'

export const PowerOfRebalancingPage = () => {
  const [showFilledBack, setShowFilledBack] = useState(false)
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  useSetAppBarTheme({ appBarTheme: 'dark' })
  const throttledScroll = throttle((scrollPosition: number) => {
    setShowFilledBack(scrollPosition >= 70)
  }, 0)

  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const scrollPosition = e.currentTarget.scrollTop || 0
    throttledScroll(scrollPosition)
  }

  const from = useMemo(() => {
    return searchParams?.get('from') ?? ''
  }, [searchParams])

  const handleBack = () => {
    if (from === 'HOME') goToHome()
    else if (from === 'APP') goBack()
    else navigate(-1)
  }

  return (
    <div onScroll={handleScroll} className='flex flex-col h-screen w-full text-[14px] overflow-y-auto'>
      <div className='flex flex-col h-max'>
        <div
          style={{ top: `calc(${getTopPadding()}px + 8px)` }}
          className={`bg-white flex items-center justify-center rounded-[50%] absolute top-0 z-1 h-9 ${showFilledBack ? 'border-[1px] border-[#EBF2F2] ml-3 w-9' : 'ml-5 w-max'}`}
        >
          <BackButton size={24} onClick={handleBack} />
        </div>
        <div style={{ paddingTop: `calc(${getTopPadding()}px + 64px)` }} className='px-5'>
          <div className='h-10 w-10' aria-hidden='true'>
            <img
              src={`${MUTUAL_FUND_DETAIL_IMAGE_URL}/power-of-rebalancing.svg`}
              alt=''
              aria-hidden='true'
              className='h-full w-full'
            />
          </div>
        </div>
        <h1 className='pt-4 px-5 text-[22px] text-[#232323] pb-1'>Power of rebalancing</h1>
      </div>
      <div className='w-full h-16 px-5 text-[#6A6E6E] font-[400]'>
        <div style={{ paddingBottom: `calc(${getBottomPadding()}px + 160px)` }} className='flex flex-col h-max w-full'>
          <div className='pt-4 leading-[150%]'>
            Most investors hesitate to exit a fund. They believe:
            <br />
            <br />
            <ul className='pl-5' style={{ listStyleType: 'disc' }}>
              <li> It will break the compounding cycle </li>
              <li>They will have to pay extra taxes/exit load </li>
            </ul>
            <br />
            <span className='font-[700] text-[#232323]'>
              {' '}
              Well, holding on to an underperforming fund costs you more.{' '}
            </span>{' '}
            Let’s understand this with an example of two investors: One holds on to the underperforming fund, the other
            rebalances to better performing funds.
          </div>
          <div className='pt-5'>
            <ExampleCard exampleType='A' />
          </div>
          <div className='pt-5'>
            <ExampleCard exampleType='B' />
          </div>
          <div className='pt-5'>
            <div
              role={GROUPED_STATIC_TEXT_ROLE}
              aria-label='Difference in CAGR, +2.51 percent, Investor B greater than Investor A'
              className='rounded-[12px] bg-[#E6F1F6] py-3 px-4 flex flex-col w-full'
            >
              <div aria-hidden='true' className='flex flex-col gap-2 w-full'>
                <div className='flex w-full justify-between'>
                  <div className='text-[#232323] font-[500]'>Difference in CAGR</div>
                  <div className='text-[#0B6B2A] font-[700]'>+2.51%</div>
                </div>
                <div className='flex items-center gap-1'>
                  <div className='text-[10px] font-[700] text-[#004D99] bg-white rounded-[50%] h-4 w-4 text-center mt-[1px]'>
                    B
                  </div>
                  <div>-</div>
                  <div className='text-[10px] font-[700] text-[#3258B9] bg-white rounded-[50%] h-4 w-4 text-center mt-[1px]'>
                    A
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className='pt-8'>
            Investor B earns 2.51% higher CAGR. <br />
            <br /> This difference in CAGR might not seem like a lot right now, but this gap only widens over time. In
            30 years, <span className='font-[700] text-[#232323]'> it’s a 2x difference in wealth!</span>
          </div>
          <div className='pt-8'>
            <TableComponent />
          </div>
          <div className='pt-8'>
            Staying in underperforming funds with lower returns means weaker compounding over the years.{' '}
            <span className='font-[700] text-[#232323]'>
              {' '}
              Don’t let the fear of exiting now, cost you decades worth of wealth.
            </span>
          </div>
        </div>
      </div>
    </div>
  )
}

const ExampleCard = ({ exampleType }: { exampleType: 'A' | 'B' }) => {
  const totalReturns = exampleType === 'A' ? '₹7,62,341' : '₹9,18,549'
  const capitalGainsTaxes = exampleType === 'A' ? '₹79,667' : '₹36,693'
  const totalWealth = exampleType === 'A' ? '₹16,82,674' : '₹18,81,856'
  const cagr = exampleType === 'A' ? '10.97%' : '13.48%'
  const returnPct = exampleType === 'A' ? '12% p.a.' : '14% p.a.'
  const investedSummary =
    exampleType === 'A'
      ? 'Invested 10L at 12% p.a. and withdrawn after 5 years'
      : 'Invested 10L at 14% p.a. rebalanced yearly and withdrawn after 5 years'

  // CNBR_01 / CNBR_04: headings stay navigable and each label-value pair is one screen reader stop.
  // A single card-wide label (CoNR_B06) hid both, so grouping stays per row.
  return (
    <div
      className={`rounded-[12px] ${exampleType === 'A' ? 'bg-[#FFF0EA]' : 'bg-[#EFF9F0]'}  text-[#6A6E6E] p-4 text-[13px]`}
    >
      <div className='flex flex-col'>
        <div className='flex'>
          <div className='flex flex-col'>
            <h2 className={`font-[700] ${exampleType === 'A' ? 'text-[#992E00]' : 'text-[#0B6B2A]'}`}>
              Investor {exampleType}
            </h2>
            <h3 className='text-[#232323] font-[700] text-[18px] leading-[140%] pt-1'>
              {exampleType === 'A' ? (
                <>
                  Stays invested in an <br /> underperforming fund
                </>
              ) : (
                <>
                  Rebalances to better <br /> performing funds
                </>
              )}
            </h3>
          </div>
          <div className='ml-auto'>{exampleType === 'A' ? <DownDoubleArrow /> : <UpDoubleArrow />}</div>
        </div>
        {/* Spoken form avoids "@" and "5Y" abbreviations */}
        <div
          role={GROUPED_STATIC_TEXT_ROLE}
          aria-label={investedSummary}
          className='text-[13px] leading-[150%] pb-4 pt-3'
        >
          <span aria-hidden='true'>
            {exampleType === 'A'
              ? ' Invested 10L @ 12%p.a. and withdrawn after 5Y'
              : 'Invested 10L @ 14% p.a. rebalanced yearly and withdrawn after 5Y'}
          </span>
        </div>
        <hr aria-hidden='true' />
        <div className='py-4 flex flex-col'>
          <NameValueTable
            nameValueRowClassName='!text-[13px]'
            valueClassName='text-[#6A6E6E] !font-[400]'
            groupForAccessibility
            gap={8}
            data={[
              {
                name: 'Invested',
                value: '₹10,00,000'
              },
              {
                name: 'Return percentage',
                nameContent: 'Return %',
                value: returnPct
              }
            ]}
          />
          <div
            role={GROUPED_STATIC_TEXT_ROLE}
            aria-label={`Total returns in 5 years${exampleType !== 'A' ? ', rebalanced yearly' : ''}, ${totalReturns}`}
            className='flex justify-between pt-[9px]'
          >
            <div aria-hidden='true'>
              Total returns in 5Y
              {exampleType !== 'A' && (
                <span className='text-[12px]'>
                  <br />
                  (rebalanced yearly)
                </span>
              )}
            </div>
            <div aria-hidden='true'>{totalReturns}</div>
          </div>
          <div
            role={GROUPED_STATIC_TEXT_ROLE}
            aria-label={`Capital gains taxes, including ₹1.25 lakh per year exemption, ${capitalGainsTaxes}`}
            className='flex justify-between pt-[9px]'
          >
            <div aria-hidden='true'>
              Capital gains taxes{' '}
              <span className='text-[12px]'>
                <br /> (incl. ₹1.25L/yr exemption)
              </span>
            </div>
            <div aria-hidden='true'>{capitalGainsTaxes}</div>
          </div>
        </div>
        <hr aria-hidden='true' />
        <div className='pt-5 pb-2'>
          <NameValueTableRow
            nameValueRowClassName='!text-[13px]'
            valueClassName='text-[#6A6E6E] !font-[400]'
            name='Total wealth (after taxes)'
            value={totalWealth}
            groupForAccessibility
            accessibilityLabel={`Total wealth after taxes, ${totalWealth}`}
          />
        </div>
        <div
          role={GROUPED_STATIC_TEXT_ROLE}
          aria-label={`Investor ${exampleType} CAGR, ${cagr}`}
          className='flex items-center text-[#232323] font-[500]'
        >
          <div
            aria-hidden='true'
            className={`text-[10px] font-[700] ${exampleType === 'A' ? 'text-[#3258B9]' : 'text-[#004D99]'}  bg-white rounded-[50%] h-4 w-4 text-center mt-[1px]`}
          >
            {exampleType}
          </div>
          <div aria-hidden='true' className='pl-1'>
            CAGR
          </div>
          <div aria-hidden='true' className='ml-auto'>
            {cagr}
          </div>
        </div>
      </div>
    </div>
  )
}

const DownDoubleArrow = () => {
  return (
    <div aria-hidden='true'>
      <RenderHTML
        innerHTMLText={`<svg xmlns="http://www.w3.org/2000/svg" width="71" height="71" viewBox="0 0 71 71" fill="none" aria-hidden="true" focusable="false">
<g opacity="0.1">
<path fillRule="evenodd" clipRule="evenodd" d="M11.9004 41.8997L35.4967 62.125L59.0929 41.8997C60.9536 40.3047 61.1691 37.5033 59.5742 35.6426C57.9793 33.7818 55.1779 33.5663 53.3171 35.1613L35.4967 50.4359L17.6762 35.1613C15.8155 33.5663 13.0141 33.7818 11.4191 35.6426C9.8242 37.5033 10.0397 40.3047 11.9004 41.8997ZM11.9004 18.2336L35.4967 38.459L59.0929 18.2337C60.9536 16.6387 61.1691 13.8373 59.5742 11.9766C57.9793 10.1158 55.1779 9.90031 53.3171 11.4953L35.4967 26.7699L17.6762 11.4952C15.8155 9.90031 13.0141 10.1158 11.4191 11.9766C9.8242 13.8373 10.0397 16.6387 11.9004 18.2336Z" fill="#E77464"/>
</g>
</svg>`}
      />
    </div>
  )
}

const UpDoubleArrow = () => {
  return (
    <div aria-hidden='true'>
      <RenderHTML
        innerHTMLText={`<svg xmlns="http://www.w3.org/2000/svg" width="72" height="71" viewBox="0 0 72 71" fill="none" aria-hidden="true" focusable="false">
  <g opacity="0.1">
    <path fillRule="evenodd" clipRule="evenodd" d="M59.5976 29.1003L36.0014 8.875L12.4052 29.1003C10.5444 30.6953 10.3289 33.4967 11.9238 35.3574C13.5188 37.2182 16.3202 37.4337 18.1809 35.8387L36.0014 20.5641L53.8218 35.8387C55.6826 37.4337 58.484 37.2182 60.0789 35.3574C61.6739 33.4967 61.4584 30.6953 59.5976 29.1003ZM59.5976 52.7663L36.0014 32.541L12.4052 52.7663C10.5444 54.3613 10.3289 57.1627 11.9238 59.0234C13.5188 60.8842 16.3202 61.0997 18.1809 59.5047L36.0014 44.2301L53.8218 59.5047C55.6826 61.0997 58.484 60.8842 60.0789 59.0234C61.6739 57.1627 61.4584 54.3613 59.5976 52.7663Z" fill="#2A9C4A"/>
  </g>
</svg>`}
      />
    </div>
  )
}

const TableComponent = () => {
  const rows = [
    ['Today', '₹10L', '₹10L', 'NA'],
    ['5Y', '₹16.8L', '₹18.8L', '1.12x'],
    ['10Y', '₹28.3L', '₹35.4L', '1.25x'],
    ['20Y', '₹80.2L', '₹1.3Cr', '1.56x'],
    ['30Y', '₹2.3Cr', '₹4.4Cr', '1.96x']
  ]

  return (
    <div className='rounded-[12px] border border-[#EBF2F2] overflow-hidden text-[13px]'>
      <table className='w-full bg-white'>
        <caption className='sr-only'>
          Comparison of investment performance between Investor A and Investor B
        </caption>
        <thead>
          <tr>
            <th className='px-3 py-2'>
              <div className='font-[400] text-left'>
                Portfolio <br /> Value
              </div>
            </th>
            <th className='px-1 py-2 w-24'>
              <div className='text-left font-[500] text-black'>
                <span className='text-[#992E00]'> Investor A </span>
                <br /> 10.97%
              </div>
            </th>
            <th className='px-1 py-2 w-24'>
              <div className='text-left font-[500] text-black'>
                <span className='text-[#0B6B2A]'> Investor B </span>
                <br /> 13.48%
              </div>
            </th>
            <th className='px-4 py-2'>
              <div className='font-[400] text-left'>
                Investor <br /> B v/s A
              </div>
            </th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row, rowIndex) => (
            <tr key={rowIndex} className={`${rowIndex === 1 ? 'bg-transparent' : 'bg-[#F6F9F9]'}`}>
              {/* #595959 on #F6F9F9 ≥4.5:1 (WCAG 1.4.3); inherited #6A6E6E measured ~4.2:1 as #767779 on #F7F8FA */}
              <td className='px-3 py-2 text-[#595959]'>{row[0]}</td>
              <td className='px-1 py-2 w-24 font-[500] text-[#232323]'>{row[1]}</td>
              <td className='px-1 py-2 w-24 font-[500] text-[#232323]'>{row[2]}</td>
              <td className='px-4 py-2 font-[500] text-[#232323]'>{row[3]}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}
